import { useRef, useEffect } from 'react';
import {
  ConversationTimelineItem,
  isUserInputItem,
  isProgressBlockItem,
  isClarificationRequestItem,
  isReportItem,
  isFollowupQuestionItem,
  isFollowupResponseItem,
} from '../../types/conversation';
import { ClarificationResponse } from '../ClarificationMessage';
import UserInputItem from './items/UserInputItem';
import ProgressBlockItem from './items/ProgressBlockItem';
import ClarificationRequestItem from './items/ClarificationRequestItem';
import ReportItem from './items/ReportItem';
import FollowupResponseItem from './items/FollowupResponseItem';

interface ConversationTimelineProps {
  timeline: ConversationTimelineItem[];
  onClarificationSubmit?: (responses: ClarificationResponse[]) => void;
  onClarificationCancel?: () => void;
  isSubmittingClarification?: boolean;
  readOnly?: boolean;
}

export function ConversationTimeline({
  timeline,
  onClarificationSubmit,
  onClarificationCancel,
  isSubmittingClarification = false,
  readOnly = false,
}: ConversationTimelineProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when timeline changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timeline]);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 space-y-4"
    >
      {timeline.map((item) => {
        if (isUserInputItem(item)) {
          return <UserInputItem key={item.id} item={item} />;
        }

        if (isProgressBlockItem(item)) {
          return <ProgressBlockItem key={item.id} item={item} />;
        }

        if (isClarificationRequestItem(item)) {
          return (
            <ClarificationRequestItem
              key={item.id}
              item={item}
              onSubmit={onClarificationSubmit}
              onCancel={onClarificationCancel}
              isSubmitting={isSubmittingClarification}
              readOnly={readOnly}
            />
          );
        }

        if (isReportItem(item)) {
          return <ReportItem key={item.id} item={item} />;
        }

        if (isFollowupQuestionItem(item)) {
          // Followup questions are displayed like user input
          return (
            <div key={item.id} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm px-4 py-3 bg-purple-600 text-white">
                <p className="text-sm whitespace-pre-wrap">{item.content}</p>
              </div>
            </div>
          );
        }

        if (isFollowupResponseItem(item)) {
          return <FollowupResponseItem key={item.id} item={item} />;
        }

        return null;
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default ConversationTimeline;
